/**
 * A yeast's fermentation scores from its type and tags (PLAN-FLAVOR D-FL4).
 *
 * Type sets the floor; tags say what the lab says the strain does. A phenolic
 * or estery tag pulls `clean` down, because a strain cannot be both. The
 * family flagships get exact scores in `overrides.ts`, which beat this.
 */

import type { AxisScores, FermentationAxis } from '@beerwright/core';

type Scores = Partial<Record<FermentationAxis, number>>;

const BY_TYPE: Readonly<Record<string, Scores>> = {
  lager: { clean: 4, sulfur: 1 },
  ale: { clean: 3, fruity: 1 },
  hybrid: { clean: 4, fruity: 1 },
  kveik: { fruity: 3, clean: 2 },
  wheat: { banana: 3, clove: 2, fruity: 2, clean: 1 },
  wine: { clean: 3, fruity: 2 },
  brett: { funk: 4, fruity: 2 },
  wild: { funk: 3, tart: 2 },
  other: {},
};

/** Tag → the axis it sets at least that high. */
const TAG_FLOORS: Readonly<Record<string, readonly (readonly [FermentationAxis, number])[]>> = {
  clean: [['clean', 4]],
  neutral: [['clean', 4]],
  crisp: [['clean', 4]],
  fruity: [['fruity', 3]],
  esters: [['fruity', 3]],
  estery: [['fruity', 3]],
  'stone-fruit': [['fruity', 3]],
  tropical: [['fruity', 3]],
  citrus: [['fruity', 2]],
  apple: [['fruity', 2]],
  banana: [['banana', 4]],
  hefeweizen: [
    ['banana', 3],
    ['clove', 3],
  ],
  clove: [['clove', 4]],
  phenolic: [['clove', 3]],
  spicy: [['pepper', 3]],
  saison: [
    ['pepper', 3],
    ['fruity', 3],
  ],
  farmhouse: [
    ['pepper', 3],
    ['fruity', 2],
  ],
  belgian: [
    ['fruity', 3],
    ['pepper', 2],
  ],
  trappist: [
    ['fruity', 3],
    ['pepper', 2],
  ],
  abbey: [
    ['fruity', 3],
    ['pepper', 2],
  ],
  witbier: [
    ['fruity', 3],
    ['pepper', 2],
    ['clove', 2],
  ],
  sulfur: [['sulfur', 3]],
  // A lab's "tart" on a Saccharomyces strain is a crisp finish, not a sour
  // beer: every witbier strain carries it. The souring words below keep 4.
  tart: [['tart', 2]],
  sour: [['tart', 4]],
  lactobacillus: [['tart', 4]],
  'kettle-sour': [['tart', 4]],
  'berliner-weisse': [['tart', 4]],
  brett: [['funk', 4]],
  funk: [['funk', 4]],
  wild: [['funk', 3]],
};

/** Axes whose presence caps `clean`. */
const UNCLEAN: readonly FermentationAxis[] = ['banana', 'clove', 'pepper', 'funk', 'tart'];

export function yeastRuleScores(yeast: {
  readonly type: string;
  readonly tags: readonly string[];
}): AxisScores<'fermentation'> {
  const scores: Scores = { ...(BY_TYPE[yeast.type] ?? {}) };
  for (const tag of yeast.tags) {
    for (const [axis, floor] of TAG_FLOORS[tag] ?? []) {
      scores[axis] = Math.max(scores[axis] ?? 0, floor);
    }
  }
  const loudest = Math.max(0, ...UNCLEAN.map((axis) => scores[axis] ?? 0));
  if (loudest >= 3) scores.clean = Math.min(scores.clean ?? 0, 1);
  return scores;
}
